import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { QAService } from './qa.service';
import { CatalogService } from '../catalog-api/catalog.service';
import { ImageUpdateService } from '../image-update/image-update.service';
import { ToasterService } from 'angular2-toaster';
import { ImageUpdateRequest } from '../catalog-api/model/image-update-request';
import { DatahealthService } from '../datahealth/datahealth.service';
import { Modal } from 'ngx-modialog/plugins/bootstrap/';
// tslint:disable-next-line:import-blacklist
import { Subscription } from 'rxjs';
import { TimerObservable } from 'rxjs/observable/TimerObservable';
import { UserDetailsService } from '../../shared/user.details.service';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'qa-manage',
    templateUrl: 'qa-manage.component.html',
    styleUrls: ['qa-manage.component.scss']
})
export class QAManageComponent implements OnInit {
    // The index for the type of results we want to show
    private selectedTypeIndex = null;

    // Related to the table
    private totalTitles = [];
    private currentTitles = [];
    private titlesLoading = false;

    // Page details
    private displayLimit = 50;
    private PAGE_SIZE = 50;
    private showMoreLoading = false;

    // Pending mappings actions and dialog
    private actionLoading = false;
    private dialog;

    // Subscription for updating QA request states
    private qaRequestsSubscription: Subscription;

    @Output() onHandleOpen = new EventEmitter();

    constructor(
        private catalogService: CatalogService,
        private imageUpdateService: ImageUpdateService,
        private qaService: QAService,
        private toasterService: ToasterService,
        private datahealthService: DatahealthService,
        private userDetailsService: UserDetailsService,
        public modal: Modal,
    ) { }

    ngOnInit() {
        // Create a 30 second trigger to update QA requests states if required
        const qaRequestsTimer = TimerObservable.create(30000, 30000);
        this.qaRequestsSubscription = qaRequestsTimer.subscribe(() => {
            if (this.selectedTypeIndex === 0) {
                this.getQARequests(0, this.displayLimit)
                    .then((qaRequests: any[]) => {
                        this.totalTitles = this.totalTitles.map((item) => {
                            const updatedQaRequest = qaRequests.find((qaRequest) => item.titleId === qaRequest.titleId);
                            if (updatedQaRequest) {
                                return updatedQaRequest;
                            }
                            // If the updatedQaRequest object does not exist, the request has been completed, do not return anything.
                        }).filter(item => item);
                        this.currentTitles = this.totalTitles.slice(0, this.displayLimit);
                    });
            }
        });
    }

    // tslint:disable-next-line:use-life-cycle-interface
    ngOnDestroy() {
        this.qaRequestsSubscription.unsubscribe();
    }

    handleGetQARequests() {
        this.selectedTypeIndex = 0;
        this.totalTitles = [];
        this.currentTitles = [];
        this.displayLimit = this.PAGE_SIZE;
        this.titlesLoading = true;
        // Get QA requests with page 0
        this.getQARequests(0)
            .then((qaRequests: any[]) => {
                this.totalTitles = qaRequests;
                this.currentTitles = this.totalTitles.slice(0, this.displayLimit);
                this.titlesLoading = false;
            })
            .catch(() => {
                // Errors are handled in the function
                this.titlesLoading = false;
            });
    }

    handleGetPendingMappings() {
        if (this.selectedTypeIndex === 1) {
            this.toasterService.pop('info', 'Pending mappings has already been selected!');
            return;
        }
        this.selectedTypeIndex = 1;
        this.titlesLoading = true;
        this.totalTitles = [];
        this.currentTitles = [];
        this.displayLimit = this.PAGE_SIZE;
        // @ts-ignore
        this.datahealthService
            .getMappingRequestTitleByState('pending')
            .then(mappingsRes => {
                // ES cannot handle more than 1024 clauses, so limit total results to 1024
                let parrotIds = Object.keys(mappingsRes).filter((item) => item !== ''
                    // We do not want bittorrent mappings here as they should be handled in the bulk infohash page
                    && mappingsRes[item].every(mappingReq => mappingReq.source !== 'bittorrent'));
                parrotIds = parrotIds.filter((v, i) => parrotIds.indexOf(v) === i).slice(0, 1024);
                this.catalogService.callEsProxyForParrotIds(
                    parrotIds,
                    ['parrot_id', 'title', 'images', 'display_name', 'catalog_state']
                )
                    .then((esRes) => {
                        this.totalTitles = esRes['hits']['hits'].map(item => {
                            return {
                                ...item._source,
                                mappings: mappingsRes[item._source.parrot_id] ? mappingsRes[item._source.parrot_id].map((mapping) => {
                                    return {
                                        ...mapping,
                                        createdOnString: (new Date(mapping.createdOn)).toLocaleString('nz')
                                    };
                                }) : []
                            };
                        });
                        this.currentTitles = this.totalTitles.slice(0, this.displayLimit);
                        if (this.currentTitles.length === 0) {
                            this.toasterService.pop('info', 'There were no titles found with pending mappings!');
                        }
                        this.titlesLoading = false;
                    })
                    .catch((err) => {
                        console.error('Failed to get pending mappings: ', err.toString());
                        this.toasterService.pop('error', 'Failed to get pending mappings');
                        this.titlesLoading = false;
                    });
            })
            .catch((err) => {
                console.error('Failed to get pending mappings: ', err.toString());
                this.toasterService.pop('error', 'Failed to get pending mappings');
                this.titlesLoading = false;
            });
    }

    handleGetPendingImages() {
        if (this.selectedTypeIndex === 2) {
            this.toasterService.pop('info', 'Pending images has already been selected!');
            return;
        }
        this.titlesLoading = true;
        this.selectedTypeIndex = 2;
        this.totalTitles = [];
        this.currentTitles = [];
        this.displayLimit = this.PAGE_SIZE;
        this.imageUpdateService
            .getImageUpdateRequest(null, ['PENDING', 'PROD_QA'])
            .then((res) => {
                // ES cannot handle more than 1024 clauses, so limit total results to 1024
                const updateRequests: ImageUpdateRequest[] = res['update_requests'];
                let parrotIds = updateRequests.map(item => item.title_id);
                parrotIds = parrotIds.filter((v, i) => parrotIds.indexOf(v) === i).slice(0, 1024);
                this.catalogService.callEsProxyForParrotIds(
                    parrotIds,
                    ['parrot_id', 'title', 'images', 'display_name', 'catalog_state']
                )
                    // tslint:disable-next-line:no-shadowed-variable
                    .then((res) => {
                        this.totalTitles = res['hits']['hits'].map(item => item._source);
                        this.getAndDisplayProdQATitles()
                            .then(() => {
                                this.titlesLoading = false;
                                this.currentTitles = this.totalTitles.slice(0, this.displayLimit);
                                if (this.currentTitles.length === 0) {
                                    this.toasterService.pop('info', 'There were no titles found with missing images!');
                                }
                            });
                    });
            })
            .catch((err) => {
                console.error('Failed to get image update requests: ', err.toString());
                this.toasterService.pop('error', 'Failed to get pending images!');
            });
    }

    handleGetTitlesMissingImages() {
        if (this.selectedTypeIndex === 3) {
            this.toasterService.pop('info', 'Titles missing images has already been selected!');
            return;
        }
        this.selectedTypeIndex = 3;
        this.titlesLoading = true;
        this.totalTitles = [];
        this.currentTitles = [];
        this.displayLimit = this.PAGE_SIZE;
        this.catalogService.getCatalogMissingImages()
            .then((res) => {
                const missingImagesResponse = res;
                this.totalTitles = missingImagesResponse.metadata.filter((title) => {
                    if (title.data.start_date) {
                        return true;
                    } else {
                        return false;
                    }
                });

                this.getAndDisplayClaimedTitles()
                    .then(() => {
                        this.titlesLoading = false;
                        this.currentTitles = this.totalTitles.slice(0, this.displayLimit);
                        if (this.currentTitles.length === 0) {
                            this.toasterService.pop('info', 'There were no titles found with missing images!');
                        }
                    });
            })
            .catch((err) => {
                console.error('Error loading titles missing images: ', err.toString());
            });
    }

    handleShowMore() {
        this.displayLimit = this.displayLimit + this.PAGE_SIZE;
        this.showMoreLoading = true;
        if (this.selectedTypeIndex === 0) {
            const page = Math.round(this.displayLimit / this.PAGE_SIZE);
            this.getQARequests(page)
                .then((qaRequests: any[]) => {
                    this.displayLimit += this.PAGE_SIZE;
                    Object.assign(this.totalTitles, qaRequests);
                    this.currentTitles = this.totalTitles.slice(0, this.displayLimit);
                    this.showMoreLoading = false;
                })
                .catch(() => {
                    this.showMoreLoading = false;
                });
        }
        this.currentTitles.push(
            ...this.totalTitles.slice(this.displayLimit - this.PAGE_SIZE, this.displayLimit)
        );
        this.showMoreLoading = false;
    }

    handleOpen(result) {
        this.onHandleOpen.emit({
            parrotId: result.parrot_id,
            title: result.title,
            displayName: result.display_name,
            catalogState: result.catalog_state,
            catalogType: result.catalog_type
        });
    }

    handleTableRowClick(result) {
        if (this.selectedTypeIndex === 1) {
            result.collapsed = !result.collapsed;
        }
    }

    handleViewMapping(mapping) {
        const source = mapping.source;
        const value = mapping.mapping_value;
        const type = mapping.mapping_type;
        if (source === 'dailymotion') {
            window.open('http://www.dailymotion.com/search/' + value);
        } else if (source === 'facebook') {
            window.open('https://www.facebook.com/' + value);
        } else if (source === 'instagram_v2') {
            window.open('https://www.instagram.com/explore/tags/' + value);
        } else if (source === 'gplus_v2') {
            window.open('https://plus.google.com/s/' + value);
        } else if (source === 'twitter') {
            if (type === 'HANDLE') {
                window.open('https://twitter.com/' + value);
            } else if (type === 'TAG') {
                window.open('https://twitter.com/search?q=%23' + value);
            } else if (type === 'SEARCH_TERM') {
                window.open('https://twitter.com/search?q=' + value);
            }
        } else if (source === 'tvmaze') {
            if (type === 'SEARCH_TERM') {
                window.open('https://www.tvmaze.com/search?q=' + value);
            } else if (type === 'ID') {
                window.open('https://www.tvmaze.com/shows/' + value);
            }
        } else if (source === 'youtube') {
            window.open('https://www.youtube.com/channel/' + value);
        } else if (source === 'gtrends') {
            window.open('https://trends.google.com/trends/explore?q=' + value);
        } else if (source === 'tumblr_v2') {
            window.open('https://www.tumblr.com/search/' + value);
        } else if (source === 'wikipedia') {
            window.open(value);
        }
    }

    handleMapping(mapping, state) {
        if (!this.userDetailsService.isMappingApprover() && !this.userDetailsService.isAdmin()) {
            this.toasterService.pop('error', 'You do not have the appropriate permissions to approve or reject mappings!');
            return;
        }

        this.actionLoading = true;

        const dialogMessage = `Are you sure you want to ${state} this mapping?`;
        this.dialog = this.modal.confirm()
            .size('sm')
            .isBlocking(false)
            .showClose(false)
            .keyboard(27)
            .body(dialogMessage)
            .okBtn('Yes')
            .cancelBtn('No')
            .open();
        this.dialog.then((resultPromise) => {
            return resultPromise.result.then(() => {
                // Modal accepted
                // @ts-ignore
                    this.datahealthService
                    .handleMappingRequest(mapping.idMappingRequest, state)
                    .then(res => {
                        mapping.state = state;
                        this.actionLoading = false;
                        this.toasterService.pop('success', 'Successfully handled mapping request');
                    })
                    .catch((err) => {
                        console.error('Failed to handle mapping request: ' + err.toString());
                        this.toasterService.pop('error', 'Failed to handle mapping request');
                    });
            },
                () => {
                    // Modal rejected
                    this.actionLoading = false;
                });
        });
    }

    getAndDisplayProdQATitles() {
        return new Promise((resolve, reject) => {
            this.imageUpdateService.getImageUpdateRequest(null, ['PROD_QA']).then((res) => {
                const claimedTitles = res.update_requests;

                this.totalTitles = this.totalTitles.map((item) => {
                    const foundTitle = claimedTitles.find(claimedTitlesItem => claimedTitlesItem.title_id === item.parrot_id);
                    if (foundTitle) {
                        item.prodQA = true;
                    }
                    return item;
                });
                resolve();
            })
                .catch((err) => {
                    console.error('Error getting claimed images: ' + err.toString());
                    resolve();
                });
        });
    }

    getAndDisplayClaimedTitles() {
        return new Promise((resolve, reject) => {
            this.imageUpdateService.getImageUpdateRequest(null, ['CLAIMED']).then((res) => {
                const claimedTitles = res.update_requests;

                this.totalTitles = this.totalTitles.map((item) => {
                    const foundTitle = claimedTitles.find(claimedTitlesItem => claimedTitlesItem.title_id === item.parrot_id);
                    if (foundTitle) {
                        item.claimed = true;
                    }
                    return item;
                });
                resolve();
            })
                .catch((err) => {
                    console.error('Error getting claimed images: ' + err.toString());
                    resolve();
                });
        });
    }

    getQARequests(page: number, size?: number) {
        return new Promise((resolve, reject) => {
            this.qaService.getQARequests('incomplete', null, 'created_on', page, size ? size : this.PAGE_SIZE)
                .then((res) => {
                    const titlesObject = {};
                    res.qa_requests.forEach((item) => {
                        titlesObject[item.title_id] = {
                            titleId: item.title_id,
                            requestUser: item.request_user,
                            type: {
                                verifyImages: (titlesObject[item.title_id] && titlesObject[item.title_id].type.verifyImages) || item.type === 'images',
                                verifyMappings: (titlesObject[item.title_id] && titlesObject[item.title_id].type.verifyMappings) || item.type === 'mappings'
                            }
                        };
                    });
                    this.catalogService.callEsProxyForParrotIds(Object.keys(titlesObject), ['parrot_id', 'title', 'display_name', 'catalog_state'])
                        // tslint:disable-next-line:no-shadowed-variable
                        .then((res) => {
                            resolve(res['hits']['hits'].map(item => {
                                return {
                                    ...titlesObject[item._source.parrot_id],
                                    parrot_id: item._source.parrot_id,
                                    title: item._source.title,
                                    display_name: item._source.display_name,
                                    catalog_state: item._source.catalog_state
                                };
                            }));
                        })
                        .catch((err) => {
                            console.error('Error calling ES when getting pending QA requests: ', err.toString());
                            this.toasterService.pop('error', 'Error calling ES when getting pending QA requests');
                            reject();
                        });
                })
                .catch((err) => {
                    console.error('Error getting pending QA requests: ' + err.toString());
                    this.toasterService.pop('error', 'Error getting pending QA requests');
                    reject();
                });
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/catalogue/qa/qa-manage.component.ts